import {
  redirect,
  useActionData,
  Form,
  useSearchParams,
  ActionFunctionArgs,
  LoaderFunctionArgs,
  Link,
  type MetaFunction,
  useParams,
} from "react-router";

import {Button, Image, Paper, PasswordInput, Text, TextInput, Title} from "@mantine/core";
import {CsrfToken} from "@digisthesia/remix-utils/csrf-token";
import {safeFormData} from "@digisthesia/remix-utils/helpers.server";

import {User} from "~gen/entities";

import {checkPassword, LoginResult, getUserFromRequest} from "~/auth.server";
import {getUserCookie} from "~/context.server";
import {getGlobalStrings, resolveLocale, resolveLocaleFromRequest, useGlobalStrings} from "~/strings/global";
import {getLangPrefix} from "~/routes/route-helpers";

function safeReturnTo(url: string | null) {
  return url && url.startsWith("/") ? url : "/";
}

export async function loader({request, url}: LoaderFunctionArgs) {
  const user = await getUserFromRequest(request);
  if (user) {
    throw redirect(safeReturnTo(url.searchParams.get("return_to")));
  }
  return {locale: resolveLocaleFromRequest(request)};
}

export async function action({request, url}: ActionFunctionArgs) {
  const globalStrings = getGlobalStrings(resolveLocaleFromRequest(request));
  const formData = await safeFormData({request});

  const email = formData.get("email") as string;
  const password = formData.get("password") as string;
  const user = await User.query().findOne({email});
  if (!user) {
    return {email, error: globalStrings.loginInvalidCredentials};
  }

  switch (await checkPassword(user, password)) {
    case LoginResult.Success:
      throw redirect(safeReturnTo(url.searchParams.get("return_to")), {
        headers: {"set-cookie": await getUserCookie().serialize(user.id)},
      });
    case LoginResult.InvalidPassword:
      return {email, error: globalStrings.loginInvalidCredentials};
    case LoginResult.TooManyAttempts:
      return {email, error: globalStrings.loginTooManyAttempts};
  }
}

export default function LoginPage() {
  const globalStrings = useGlobalStrings();
  const {email, error} = useActionData<typeof action>() ?? {};
  const [searchParams] = useSearchParams();
  const {lang} = useParams();
  const langPrefix = getLangPrefix(lang);

  return (
    <div className="login-wrapper">
      <Paper className="login-form">
        <Image mx="auto" w={200} src="http://static.digisthesia.com/hexploit/images/logo_hexploit_500.webp" />
        <Title order={2} ta="center">
          {globalStrings.loginHeading}
        </Title>
        <Form method="POST">
          <CsrfToken />
          {error && (
            <div className="row alert alert-danger my-3" role="alert">
              {error}
            </div>
          )}

          <TextInput
            defaultValue={email ?? searchParams.get("email") ?? ""}
            label={globalStrings.loginEmailLabel}
            id="email"
            name="email"
            type="email"
            placeholder={globalStrings.loginEmailPlaceholder}
            size="md"
            radius="md"
          />
          <PasswordInput
            label={globalStrings.loginPasswordLabel}
            placeholder={globalStrings.loginPasswordPlaceholder}
            id="password"
            name="password"
            mt="md"
            size="md"
            radius="md"
          />

          <Link to={`${langPrefix}/forgot-password`}>{globalStrings.loginForgotPassword}</Link>

          <Button fullWidth mt="xl" size="md" radius="md" type="submit">
            {globalStrings.loginSubmit}
          </Button>
        </Form>

        <Text ta="center" mt="md">
          {globalStrings.loginNoAccount} <Link to={`${langPrefix}/signup`}>{globalStrings.loginCreateAccountLink}</Link>
        </Text>
      </Paper>
    </div>
  );
}

export const meta: MetaFunction<typeof loader> = ({loaderData}) => {
  const globalStrings = getGlobalStrings(resolveLocale(loaderData?.locale));
  return [{title: globalStrings.loginTitle}];
};
